import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import LoginModal from './LoginModal';
import { supabase } from '../lib/supabase';

function Footer() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [contactPhone, setContactPhone] = useState('+234 (0) 803 123 4567');
  const [contactEmail, setContactEmail] = useState('info@ibomopen.com');
  const [isLeaderboardPublished, setIsLeaderboardPublished] = useState(true);
  const [isPressPublished, setIsPressPublished] = useState(true);
  const [isSchedulePublished, setIsSchedulePublished] = useState(true);
  const [isHistoryPublished, setIsHistoryPublished] = useState(true);
  const [isGalleryPublished, setIsGalleryPublished] = useState(true);
  const [socialMedia, setSocialMedia] = useState({
    facebook: 'https://facebook.com/ibomopen',
    twitter: 'https://twitter.com/ibomopen',
    instagram: 'https://instagram.com/ibomopen'
  });

  useEffect(() => {
    fetchSettings();
    checkLeaderboardPublishStatus();
    checkPressPublishStatus();
    checkSchedulePublishStatus();
    checkHistoryPublishStatus();
    checkGalleryPublishStatus();
  }, []);

  const checkGalleryPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gallery_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking gallery publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsGalleryPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking gallery publish status:', err);
    }
  };
  const checkPressPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'press_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking press publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsPressPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking press publish status:', err);
    }
  };

  const checkSchedulePublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'schedule_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking schedule publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsSchedulePublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking schedule publish status:', err);
    }
  };

  const checkHistoryPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'history_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking history publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsHistoryPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking history publish status:', err);
    }
  };

  const checkLeaderboardPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'leaderboard_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking leaderboard publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsLeaderboardPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking leaderboard publish status:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      // Fetch contact phone
      const { data: phoneData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'contact_phone')
        .single();
      
      if (phoneData?.value) {
        // Handle as string, not JSON
        setContactPhone(String(phoneData.value));
      }

      // Fetch contact email
      const { data: emailData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'contact_email')
        .single();
      
      if (emailData?.value) {
        // Handle as string, not JSON
        setContactEmail(String(emailData.value));
      }

      // Fetch social media links
      const { data: socialData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'social_media')
        .single();
      
      if (socialData?.value) {
        // This one is actually JSON, so we can use it directly
        if (typeof socialData.value === 'object') {
          setSocialMedia(socialData.value);
        } else {
          // If it's a string, try to parse it
          try {
            setSocialMedia(JSON.parse(socialData.value));
          } catch (e) {
            console.error('Error parsing social media data:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  return (
    <footer className="bg-golf-green text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          <div className="space-y-4">
            <h3 className="text-2xl font-display font-bold mb-4">Ibom Open</h3>
            <p className="text-golf-sand">
              Nigeria's premier golf tournament at the prestigious
              Ibom Golf & Country Club
            </p>
            <button 
              className="text-sm text-golf-sand hover:text-white transition-colors"
              onClick={() => setIsLoginModalOpen(true)}
            >
              Admin Login
            </button>
            <div className="flex space-x-4 mt-4">
              <a href={socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-golf-sand hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
              </a>
              <a href={socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-golf-sand hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-golf-sand hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-golf-sand hover:text-white transition-colors">
                  About Tournament
                </Link>
              </li>
              {isSchedulePublished && (
                <li>
                  <Link to="/schedule" className="text-golf-sand hover:text-white transition-colors">
                    Tournament Schedule
                  </Link>
                </li>
              )}
              {isHistoryPublished && (
                <li>
                  <Link to="/history" className="text-golf-sand hover:text-white transition-colors">
                    Tournament History
                  </Link>
                </li>
              )}
              {isGalleryPublished && (
                <li>
                  <Link to="/gallery" className="text-golf-sand hover:text-white transition-colors">
                    Photo Gallery
                  </Link>
                </li>
              )}
              {isLeaderboardPublished && (
                <li>
                  <Link to="/leaderboard" className="text-golf-sand hover:text-white transition-colors">
                    Leaderboard
                  </Link>
                </li>
              )}
              {isPressPublished && (
                <li>
                  <Link to="/press" className="text-golf-sand hover:text-white transition-colors">
                    Press Registration
                  </Link>
                </li>
              )}
              <li>
                <Link to="/registration" className="text-golf-sand hover:text-white transition-colors">
                  Player Registration
                </Link>
              </li>
              <li>
                <Link to="/sponsors" className="text-golf-sand hover:text-white transition-colors">
                  Partnership Opportunities
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-golf-sand hover:text-white transition-colors">
                  Support
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-golf-sand">
              <li>Ibom Golf & Country Club</li>
              <li>Uyo, Akwa Ibom State</li>
              <li>Nigeria</li>
              <li>Email: {contactEmail}</li>
              <li>Phone: {contactPhone}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="text-golf-sand hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-golf-sand hover:text-white transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => {
                    localStorage.removeItem('cookieConsent');
                    window.location.reload();
                  }}
                  className="text-golf-sand hover:text-white transition-colors"
                >
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-golf-sand text-center text-golf-sand">
          <p>&copy; 2004 &ndash; {new Date().getFullYear()} Ibom Open. All rights reserved.</p>
        </div>
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </footer>
  );
}

export default Footer;